import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api.js';
import { t } from '../i18n/index.js';
import { SettingsAdmin } from './admin/SettingsAdmin.js';
import { GamesAdmin } from './admin/GamesAdmin.js';
import { MessagesAdmin } from './admin/MessagesAdmin.js';
import { ClosureAdmin } from './admin/ClosureAdmin.js';
import { BackupStatus } from './admin/BackupStatus.js';
import { RadioAdmin } from './admin/RadioAdmin.js';
import { BingoAdmin } from './admin/BingoAdmin.js';
import { SmsAdmin } from './admin/SmsAdmin.js';
import { CoinsAdmin } from './admin/CoinsAdmin.js';
import { InvitesAdmin } from './admin/InvitesAdmin.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { CopyField } from '../components/CopyField.js';
import { joinUrl } from '../joinLinks.js';
import { limits, type AdminUserView, type JoinLinkView, type PublicUser, type Role } from '../../shared/protocol.js';

type Flash = { ok: boolean; text: string } | null;

/** A link that was just created: shown only once (decision 2026-09-28). */
type NewLink = { title: string; note: string; url: string; expiresAt: number };

function errorText(err: unknown): string {
  return t.errors[err instanceof ApiError ? err.code : 'network'];
}

function formatTime(iso: string | number | null): string {
  if (iso === null) return t.admin.never;
  return new Date(iso).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' });
}

const ADMIN_TABS = ['users', 'invites', 'settings', 'radio', 'bingo', 'sms', 'coins', 'games', 'messages', 'closure'] as const;
type AdminTab = (typeof ADMIN_TABS)[number];
const TAB_KEY = 'areena.adminTab';

/** The last tab is remembered, so that a reload (e.g. the bingo host after a crash) returns to it. */
function storedTab(): AdminTab {
  try {
    const v = localStorage.getItem(TAB_KEY);
    return (ADMIN_TABS as readonly string[]).includes(v ?? '') ? (v as AdminTab) : 'users';
  } catch {
    return 'users';
  }
}

/** Administration: users, Game Arena settings and active games (suunnitelma.md section 4). */
export function AdminView(props: { me: PublicUser }) {
  const [tab, setTabState] = useState<AdminTab>(storedTab());
  const setTab = (next: AdminTab) => {
    setTabState(next);
    try {
      localStorage.setItem(TAB_KEY, next);
    } catch {
      // Storage unavailable: the tab is not remembered.
    }
  };
  // A running bingo game is announced on the other tabs (user request 2026-09-30).
  const [bingoRunning, setBingoRunning] = useState(false);
  useEffect(() => {
    const check = () =>
      fetch('/api/bingo/state', { cache: 'no-store' })
        .then((r) => r.json())
        .then((s: { game: { status: string } | null }) => setBingoRunning(s.game?.status === 'running'))
        .catch(() => {});
    void check();
    const timer = setInterval(check, 15_000);
    return () => clearInterval(timer);
  }, []);
  return (
    <div class="stack">
      <div class="tabs" role="tablist">
        {ADMIN_TABS.map((id) => (
          <button role="tab" aria-selected={tab === id} class={`tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>
            {t.admin.tabs[id]}
            {id === 'bingo' && bingoRunning && <span class="tab-live" aria-hidden="true" />}
          </button>
        ))}
      </div>
      {bingoRunning && tab !== 'bingo' && (
        <div class="bingo-running-bar" role="status">
          <span>🎱 {t.admin.bingo.runningBar}</span>
          <button class="btn btn-small btn-primary" onClick={() => setTab('bingo')}>
            {t.admin.bingo.backToGame}
          </button>
        </div>
      )}
      {tab === 'users' && <UsersAdmin me={props.me} />}
      {tab === 'invites' && <InvitesAdmin />}
      {tab === 'settings' && <SettingsAdmin />}
      {tab === 'radio' && <RadioAdmin />}
      {tab === 'bingo' && <BingoAdmin admin />}
      {tab === 'sms' && <SmsAdmin />}
      {tab === 'coins' && <CoinsAdmin />}
      {tab === 'games' && <GamesAdmin />}
      {tab === 'messages' && <MessagesAdmin />}
      {tab === 'closure' && (
        <>
          <ClosureAdmin />
          <BackupStatus />
        </>
      )}
    </div>
  );
}

function UsersAdmin(props: { me: PublicUser }) {
  const [users, setUsers] = useState<AdminUserView[]>([]);
  const [links, setLinks] = useState<JoinLinkView[]>([]);
  const [newLink, setNewLink] = useState<NewLink | null>(null);
  const [flash, setFlash] = useState<Flash>(null);

  const load = () =>
    Promise.all([
      api<{ users: AdminUserView[] }>('GET', '/api/admin/users').then((r) => setUsers(r.users)),
      api<{ links: JoinLinkView[] }>('GET', '/api/admin/join-links').then((r) => setLinks(r.links)),
    ]).catch((err) => setFlash({ ok: false, text: errorText(err) }));

  useEffect(() => {
    void load();
  }, []);

  const update = async (user: AdminUserView, changes: { isLocked?: boolean; role?: Role; musicAllowed?: boolean; bingoHost?: boolean }) => {
    try {
      await api('PATCH', `/api/admin/users/${user.id}`, changes);
      setFlash(null);
      await load();
    } catch (err) {
      setFlash({ ok: false, text: errorText(err) });
    }
  };

  const resetLink = async (user: AdminUserView) => {
    try {
      const r = await api<{ token: string; expiresAt: number }>('POST', `/api/admin/users/${user.id}/reset-link`);
      setFlash(null);
      setNewLink({ title: t.admin.resetCreated(user.displayName), note: t.admin.resetOnce, url: joinUrl('reset', r.token), expiresAt: r.expiresAt });
      scrollTo({ top: 0, behavior: 'smooth' });
      await load();
    } catch (err) {
      setFlash({ ok: false, text: errorText(err) });
    }
  };

  const cancelLink = async (link: JoinLinkView) => {
    try {
      await api('DELETE', `/api/admin/join-links/${link.id}`);
      await load();
    } catch (err) {
      setFlash({ ok: false, text: errorText(err) });
    }
  };

  return (
    <div class="stack">
      <CreateJoinLinkForm
        onCreated={(link) => {
          setNewLink(link);
          void load();
        }}
      />
      {newLink && (
        <section class="card one-time-link" role="status">
          <h3>{newLink.title}</h3>
          <p class="hint">{newLink.note}</p>
          <CopyField value={newLink.url} label={newLink.title} />
          <div class="row-between">
            <span class="hint">{t.admin.linkExpires(formatTime(newLink.expiresAt))}</span>
            <button class="btn btn-ghost btn-small" onClick={() => setNewLink(null)}>
              {t.admin.close}
            </button>
          </div>
        </section>
      )}
      {links.length > 0 && (
        <section class="card">
          <h3>{t.admin.linksTitle}</h3>
          <ul class="user-list">
            {links.map((l) => (
              <li key={l.id} class="user-row">
                <div class="user-main">
                  <div class="user-name">
                    {l.displayName ?? `@${l.username}`}
                    {l.displayName && <span class="muted"> @{l.username}</span>}
                  </div>
                  <div class="user-sub">
                    {t.admin.linkPurpose[l.purpose]}
                    {l.purpose === 'join' && ` · ${t.roles[l.role]}`} · {t.admin.linkExpires(formatTime(l.expiresAt))}
                  </div>
                </div>
                <button class="btn btn-small" onClick={() => void cancelLink(l)}>
                  {t.admin.cancelLink}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section class="card">
        <h2>{t.admin.title}</h2>
        {flash && (
          <p class={flash.ok ? 'success' : 'error'} role="status">
            {flash.text}
          </p>
        )}
        <ul class="user-list">
          {users.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              isMe={u.id === props.me.id}
              onUpdate={update}
              onResetLink={resetLink}
              onFlash={setFlash}
              onDeleted={() => void load()}
            />
          ))}
        </ul>
      </section>
    </div>
  );
}

function UserRow(props: {
  user: AdminUserView;
  isMe: boolean;
  onUpdate: (u: AdminUserView, changes: { isLocked?: boolean; role?: Role; musicAllowed?: boolean; bingoHost?: boolean }) => void;
  onResetLink: (u: AdminUserView) => void;
  onFlash: (f: Flash) => void;
  onDeleted: () => void;
}) {
  const { user, isMe } = props;
  const [confirmDelete, setConfirmDelete] = useState(false);

  const remove = async () => {
    setConfirmDelete(false);
    try {
      await api('DELETE', `/api/admin/users/${user.id}`);
      props.onFlash({ ok: true, text: t.admin.deleted(user.displayName) });
      props.onDeleted();
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };

  const joinMethod = t.admin.joinMethod[user.joinMethod] + (user.inviteBatch ? ` · ${user.inviteBatch}` : '');

  return (
    <li class={`user-row ${user.isLocked ? 'is-locked' : ''}`}>
      <div class="user-main">
        <div class="user-name">
          {user.displayName}
          {isMe && <span class="muted"> ({t.admin.you})</span>}
        </div>
        <div class="user-sub">
          @{user.username} · {t.roles[user.role]} · {user.isLocked ? t.admin.locked : t.admin.active} ·{' '}
          {t.admin.lastLogin}: {formatTime(user.lastLoginAt)}
          {user.mustChangePassword && <span class="badge badge-warn">{t.admin.mustChange}</span>}
        </div>
        <div class="user-sub">
          <span title={t.admin.joinMethodTitle}>{joinMethod}</span> · {t.admin.joined} {formatTime(user.createdAt)}
          {(user.musicAllowed || user.role === 'admin') && (
            <span class="badge badge-music">🎵 {user.role === 'admin' ? t.admin.musicAlways : t.admin.musicBadge}</span>
          )}
          {user.bingoHost && user.role !== 'admin' && <span class="badge badge-bingo">🎱 {t.admin.bingoHostBadge}</span>}
        </div>
      </div>
      <div class="user-actions">
        {user.role !== 'admin' && user.joinMethod !== 'guest' && (
          <button
            class={`btn btn-small ${user.musicAllowed ? 'btn-music' : ''}`}
            onClick={() => props.onUpdate(user, { musicAllowed: !user.musicAllowed })}
            title={t.admin.musicTitle}
          >
            🎵 {user.musicAllowed ? t.admin.musicRevoke : t.admin.musicAllow}
          </button>
        )}
        {/* Bingo host (2026-10-01): may run the bingo; can be taken away at any time. */}
        {user.role !== 'admin' && user.joinMethod !== 'guest' && (
          <button
            class={`btn btn-small ${user.bingoHost ? 'btn-bingo' : ''}`}
            onClick={() => props.onUpdate(user, { bingoHost: !user.bingoHost })}
            title={t.admin.bingoHostTitle}
          >
            🎱 {user.bingoHost ? t.admin.bingoHostRevoke : t.admin.bingoHostGrant}
          </button>
        )}
        {!isMe && (
          <>
            <button class="btn btn-small" onClick={() => props.onUpdate(user, { isLocked: !user.isLocked })}>
              {user.isLocked ? t.admin.unlock : t.admin.lock}
            </button>
            {user.joinMethod !== 'guest' && (
              <>
                <select
                  class="role-select"
                  value={user.role}
                  aria-label={t.admin.role}
                  onChange={(e) => props.onUpdate(user, { role: e.currentTarget.value as Role })}
                >
                  {(['player', 'moderator', 'admin'] as const).map((r) => (
                    <option value={r}>{t.roles[r]}</option>
                  ))}
                </select>
                <button class="btn btn-small" onClick={() => props.onResetLink(user)}>
                  {t.admin.resetPassword}
                </button>
              </>
            )}
            <button class="btn btn-small btn-danger" onClick={() => setConfirmDelete(true)}>
              {t.admin.delete}
            </button>
          </>
        )}
      </div>
      {confirmDelete && (
        <ConfirmDialog
          title={t.admin.deleteTitle(user.displayName)}
          confirmLabel={t.admin.deleteConfirm}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={() => void remove()}
          onCancel={() => setConfirmDelete(false)}
        >
          <p>{t.admin.deleteInfo}</p>
        </ConfirmDialog>
      )}
    </li>
  );
}

/** New user: only the username (and role); the player completes the account through the link. */
function CreateJoinLinkForm(props: { onCreated: (link: NewLink) => void }) {
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<Role>('player');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const name = username.trim();
      const r = await api<{ token: string; expiresAt: number }>('POST', '/api/admin/join-links', { username: name, role });
      props.onCreated({ title: t.admin.linkCreated(name), note: t.admin.linkOnce, url: joinUrl('link', r.token), expiresAt: r.expiresAt });
      setUsername('');
      setRole('player');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form class="card form" onSubmit={submit}>
      <h2>{t.admin.newUser}</h2>
      <p class="hint">{t.admin.newUserInfo}</p>
      <div class="grid-2">
        <label>
          {t.admin.username}
          <input
            value={username}
            onInput={(e) => setUsername(e.currentTarget.value)}
            pattern={limits.usernamePattern.slice(1, -1)}
            autocomplete="off"
            autocapitalize="none"
            spellcheck={false}
            required
          />
          <small>{t.admin.hints.username}</small>
        </label>
        <label>
          {t.admin.role}
          <select value={role} onChange={(e) => setRole(e.currentTarget.value as Role)}>
            <option value="player">{t.roles.player}</option>
            <option value="moderator">{t.roles.moderator}</option>
            <option value="admin">{t.roles.admin}</option>
          </select>
        </label>
      </div>
      {error && (
        <p class="error" role="alert">
          {error}
        </p>
      )}
      <button class="btn btn-primary" type="submit" disabled={busy}>
        {t.admin.createLink}
      </button>
    </form>
  );
}
